/**
 * US-E1-002 + US-E1-003 — Login by email+password / OAuth + remember
 *
 * Feedback strategy (UX-spec, согласовано с AD/UI/UX 2026-05-06):
 *  • Транзиентные ошибки (wrong_credentials / blocked / network /
 *    empty_fields) → Toast (bottom-right). Не отнимают площадь у формы,
 *    стандарт Slack/Telegram/GitHub.
 *  • Sticky-кейс с CTA «email_not_verified» → inline Notification на форме
 *    (нужна кнопка Resend, который не уместен в Toast).
 */
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { authApi, type LoginRequest } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useAuth } from '@/stores/auth'
import { redirectAfterAuth } from '@/lib/authRedirect'
import { Icon } from '@/components/icons/Icon'
import { OAuthButtons } from '@/components/auth/OAuthButtons'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { ResendVerificationButton } from '@/components/auth/ResendVerificationButton'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { TextInput } from '@/components/auth/TextInput'
import { Button } from '@/components/ui/Button'
import { Notification } from '@/components/ui/Notification'
import { toast } from '@/components/ui/toast'
import { EmailRequirementsList } from '@/components/ui/EmailRequirementsList'
import { validateEmail } from '@/lib/validation'
import { useIsPinCapable } from '@/lib/device'

export function Login() {
  const { t } = useTranslation()
  const setSession = useAuth((s) => s.setSession)

  // Pre-fill email from ?email= (verify success → login flow, P1a from protocol №031)
  const initialEmail = (() => {
    if (typeof window === 'undefined') return ''
    const fromUrl = new URLSearchParams(window.location.search).get('email')
    return fromUrl ?? ''
  })()
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  // Только sticky-кейс с CTA остаётся в state — остальные летят через Toast.
  const [unverified, setUnverified] = useState(false)

  // PIN-availability hint — гейт по device class (mobile/tablet only).
  // Phase 1 (v0.2.0) скрывает PIN на desktop; Phase 2 (US-E1-009) откроет
  // его обратно когда появится auto-lock в Settings → Security.
  const isPinCapable = useIsPinCapable()
  const hasPinHint = (() => {
    if (typeof window === 'undefined') return false
    if (!isPinCapable) return false
    const deviceId = localStorage.getItem('agentceo_device_id')
    const lastEmail = localStorage.getItem('agentceo_last_login_email')
    return Boolean(deviceId && lastEmail)
  })()

  const m = useMutation({
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: (res) => {
      setSession(res)
      // Remember email so PIN-login screen can prefill it next time.
      localStorage.setItem('agentceo_last_login_email', res.user.email)
      redirectAfterAuth(res.user)
    },
    onError: (e: ApiError) => {
      // discriminate by backend error.code first, fallback to status
      if (e.code === 'email_not_verified' || e.status === 403) {
        setUnverified(true)
      } else if (e.status === 401) {
        toast.error(
          t('auth.login.wrong_title', 'Неверный email или пароль'),
          t('auth.login.wrong_body', 'Проверь данные и попробуй ещё раз. Забыл пароль — восстанови по ссылке выше.')
        )
      } else if (e.status === 429) {
        toast.warning(
          t('auth.login.blocked_title', 'Слишком много попыток'),
          t('auth.login.blocked_body', 'Подожди 15 минут или восстанови пароль.')
        )
      } else {
        toast.error(
          t('auth.login.network_title', 'Ошибка сети'),
          t('auth.login.network_body', 'Не удалось войти. Попробуй ещё раз.')
        )
      }
    },
  })

  // Дополнительный кейс: вернулись из подтверждения email с ?verified=1
  useEffect(() => {
    if (typeof window === 'undefined') return
    const verified = new URLSearchParams(window.location.search).get('verified')
    if (verified === '1') {
      toast.success(t('auth.login.verified_ok', 'Email подтверждён, можно войти'))
    }
  }, [t])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setUnverified(false)
    if (!email || !password) {
      toast.warning(
        t('auth.login.empty_title', 'Заполни поля'),
        t('auth.login.empty_body', 'Email и пароль обязательны для входа.')
      )
      return
    }
    m.mutate({ email: email.toLowerCase().trim(), password, remember })
  }

  return (
    <AuthLayout side="login">
      <a href="/" className="hidden lg:flex w-fit items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium mb-8">
        <Icon name="arrow-left" size={16} />
        {t('common.back', 'Назад')}
      </a>

      <h1 className="text-3xl font-bold font-display text-text-primary mb-2 text-center lg:text-left">
        {t('auth.login.title', 'С возвращением 👋')}
      </h1>
      <p className="text-text-secondary text-sm mb-10 text-center lg:text-left">
        {t('auth.login.sub', 'CEO ждёт. Войди и принимай решения.')}
      </p>

      {hasPinHint && (
        <div className="mb-6 p-4 rounded-lg bg-surface-subtle border border-border flex items-center justify-between gap-3">
          <span className="text-sm text-text-secondary">
            {t('auth.login.pin_hint', 'Установлен PIN на этом устройстве?')}
          </span>
          <a
            href="/auth/pin"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-link"
          >
            <Icon name="lock" size={14} />
            {t('auth.login.pin_cta', 'Войти по PIN')}
          </a>
        </div>
      )}

      {unverified && (
        <div className="mb-4">
          <Notification
            variant="warning"
            title={t('auth.login.unverified_title', 'Подтверди email')}
            description={t('auth.login.unverified_body', 'Мы отправили письмо при регистрации. Перейди по ссылке из письма, чтобы войти.')}
            action={email ? <ResendVerificationButton email={email.toLowerCase().trim()} /> : undefined}
            onDismiss={() => setUnverified(false)}
          />
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="login-email" className="block text-sm font-semibold text-text-primary mb-2">Email</label>
          <TextInput
            id="login-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={setEmail}
            placeholder="alex@company.ru"
            leftSlot={<Icon name="mail" size={20} />}
            state={email && !validateEmail(email) ? 'success' : email && validateEmail(email) ? 'error' : 'default'}
          />
          <div className="mt-2"><EmailRequirementsList value={email} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="login-password" className="text-sm font-semibold text-text-primary">{t('auth.password', 'Пароль')}</label>
            <a href="/auth/recover" className="inline-flex items-center min-h-[24px] py-1 text-xs text-text-link font-medium">{t('auth.forgot', 'Забыли пароль?')}</a>
          </div>
          <PasswordInput
            id="login-password"
            name="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
            placeholder="Введи пароль"
            leftSlot={<Icon name="lock" size={20} />}
          />
        </div>
        <label htmlFor="login-remember" className="flex items-center gap-2.5 cursor-pointer">
          <input
            id="login-remember"
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            /* WCAG 2.5.5 Target Size AA — 24×24 минимум */
            className="w-6 h-6 accent-accent-brand-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-border-focus/30 rounded"
          />
          <span className="text-sm text-text-secondary">{t('auth.remember', 'Запомнить на этом устройстве (30 дней)')}</span>
        </label>
        <Button type="submit" variant="primary" size="lg" fullWidth loading={m.isPending}>
          {m.isPending ? t('auth.login.loading', 'Входим…') : t('auth.login.cta', 'Войти')}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-12 text-text-muted text-xs font-medium uppercase tracking-wider">
        <span className="flex-1 h-px bg-border"/>
        {t('auth.or_oauth', 'или быстрее через')}
        <span className="flex-1 h-px bg-border"/>
      </div>

      <OAuthButtons caption={null} />

      <p className="text-center text-sm text-text-secondary mt-8">
        {t('auth.no_account', 'Нет аккаунта?')}{' '}
        <a href="/auth/register" className="inline-flex items-center min-h-[24px] px-1 text-text-link font-semibold">{t('auth.register_here', 'Создать')}</a>
      </p>
    </AuthLayout>
  )
}
